// src/Context/ThemeContext.tsx
import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useEffect,
} from "react";
import { theme } from "antd";
import organizationService from "../../Services/organization.service";
import { Organization } from "../../Interface/Index";

interface ThemeContextType {
  isDarkMode: boolean;
  toggleTheme: () => void;
  customTheme: {
    themeName: string;
    primaryColor: string;
    secondaryColor: string;
  };
  setCustomTheme: (theme: {
    themeName: string;
    primaryColor: string;
    secondaryColor: string;
  }) => void;
  organizationDetails: Organization | null;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Retrieve the saved theme mode from localStorage
    const savedTheme = localStorage.getItem("isDarkMode");
    return savedTheme === "true"; // Convert string to boolean
  });
  const [customTheme, setCustomThemeState] = useState({
    themeName: "",
    primaryColor: "",
    secondaryColor: "",
  });
  const [organizationDetails, setOrganizationDetails] =
    useState<Organization | null>(null);

  const toggleTheme = () => {
    setIsDarkMode((prevMode) => {
      const newMode = !prevMode;
      // Save the new theme mode to localStorage
      localStorage.setItem("isDarkMode", newMode.toString());
      return newMode;
    });
  };

  const setCustomTheme = (theme: {
    themeName: string;
    primaryColor: string;
    secondaryColor: string;
  }) => {
    setCustomThemeState(theme);
  };

  useEffect(() => {
    fetchOrganizationDetails();
  }, []);

  const fetchOrganizationDetails = async () => {
    try {
      const data = await organizationService.getOrganizationDetails();
      if (data?.theme) {
        setCustomTheme(data?.theme);
      }
    } catch (error) {
      console.error("Error fetching organization details:", error);
    }
  };

  return (
    <ThemeContext.Provider
      value={{
        isDarkMode,
        toggleTheme,
        customTheme,
        setCustomTheme,
        organizationDetails,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
